import { useContext, useEffect } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router';
import { ConfirmPopup, type ConfirmPopupData } from './ConfirmPopup';
import { openDialog } from './dialog';

/** Closing or reloading the tab with unsaved changes asks first. */
function useBeforeUnloadGuard(when: boolean) {
  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Some browsers still need a returnValue to show their own prompt.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);
}

/** Moving to another page inside the app with unsaved changes asks first. Needs the app's data router. */
function RouterBlocker({ when }: { when: boolean }) {
  const blocker = useBlocker(when);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    void openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
      data: { title: 'Unsaved changes', message: 'Leave this page? Your unsaved changes will be lost.' },
    })
      .afterClosed()
      .then((leave) => (leave === true ? blocker.proceed() : blocker.reset()));
  }, [blocker]);
  return null;
}

/**
 * Render on a page that has an always-editable form: while `when` (the form is dirty) is true, closing the tab or leaving
 * the page asks before the changes are lost. Outside the app's data router (e.g. in a test) only the tab guard applies.
 */
export function LeaveGuard({ when }: { when: boolean }) {
  useBeforeUnloadGuard(when);
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  return dataRouter ? <RouterBlocker when={when} /> : null;
}
